 import React, { useEffect } from 'react';
import { Col, Form, Row, Skeleton } from 'antd';
import { CheckboxOptionType } from 'antd/es/checkbox';
import { I18n } from '@aws-amplify/core';
import { v4 as uuidv4 } from 'uuid';

import type { AdditionalItemsResults, GetAdditionalItemOptionsResponse, GetAdditionalToolScore } from '@/hooks';
import { useApplicationContext } from '@/context/v1/Application/context';
import { useStepKeysContext } from '../DefaultSelects/context';
import { useCalculateAdditionalToolScore, useGetAdditionalItemsOptions } from '@/hooks';
import { ConditionCard } from '../../ConditionCard';
import { Text } from '@/components/Typography';
import { AditionalItemProps } from './types';

import * as S from '@/components/views/EwaJdsD86/Report/styles';

const { useWatch, useFormInstance } = Form;

export function RadioAndRadioAndRadio({ additionalItem }: Readonly<AditionalItemProps>): JSX.Element {
	const { id, title_1, title_2, title_3 } = additionalItem;

	const form = useFormInstance();
	const { formFieldName, stepKey } = useStepKeysContext();
	const { organization, company } = useApplicationContext();

	const valueOne = useWatch([...formFieldName, 'additional_items', id, 'value_1']);
	const valueTwo = useWatch([...formFieldName, 'additional_items', id, 'value_2']);
	const valueThree = useWatch([...formFieldName, 'additional_items', id, 'value_3']);

	const {
		data: options,
		isLoading: isGettingOptions,
		isFetching: isFetchingOptions
	} = useGetAdditionalItemsOptions({
		company_id: company?.id,
		organization_id: organization?.id,
		custom_report_step_key_id: stepKey.id
	});

	const payload: GetAdditionalToolScore = {
		value_1: valueOne,
		value_2: valueTwo,
		value_3: valueThree,
		company_id: company?.id,
		organization_id: organization?.id,
		custom_report_step_key_id: stepKey.id
	};

	const { data: additionalToolScore, isFetching: isFetchingScore } = useCalculateAdditionalToolScore(payload);

	const areAllAnswered = isAnswered(valueOne) && isAnswered(valueTwo) && isAnswered(valueThree);

	const questions = [
		{ title: title_1, name: 'value_1' },
		{ title: title_2, name: 'value_2' },
		{ title: title_3, name: 'value_3' }
	];

	useEffect(() => {
		const result: AdditionalItemsResults[] = [
			{
				id: uuidv4(),
				value_1: valueOne,
				value_2: valueTwo,
				value_3: valueThree,
				result: additionalToolScore?.result ?? 0
			}
		];

		form.setFieldValue([...formFieldName, 'additional_items', id, 'results'], result);
	}, [additionalToolScore]);

	function isAnswered(value?: number): boolean {
		return value !== null && value !== undefined;
	}

	function handleOptions(data: GetAdditionalItemOptionsResponse[]): CheckboxOptionType[] {
		return data.map(({ description, score }) => ({
			value: Number(score),
			label: I18n.get(`${description}`)
		}));
	}

	if (isGettingOptions) {
		return (
			<Row gutter={[0, 15]}>
				<Col span={18}>
					<Skeleton active />
				</Col>
				<Col span={22}>
					<Skeleton active paragraph />
				</Col>
			</Row>
		);
	}

	const radioOptions = handleOptions(options);

	return (
		<Row gutter={[0, 15]} key={id}>
			{questions.map((question) => (
				<Col span={24} key={question.name}>
					<Row gutter={[0, 10]}>
						<S.RadioButtonTitle span={24}>
							<Text strong>{I18n.get(question.title)}</Text>
						</S.RadioButtonTitle>
						<Col span={16}>
							<Form.Item
								style={{ margin: 0 }}
								name={[...formFieldName, 'additional_items', id, question.name]}
							>
								<S.CustomRadioGroup options={radioOptions} />
							</Form.Item>
						</Col>
					</Row>
				</Col>
			))}
			<Col span={22}>
				<ConditionCard
					result={areAllAnswered ? additionalToolScore?.result : 0}
					isLoading={isGettingOptions || isFetchingOptions || isFetchingScore}
				/>
			</Col>
		</Row>
	);
}
